import { APPLICATION_FUNCTION_EXTERNAL_ID, PageArea } from '..'
import Loading from '../../components/process-state-notifications/Loading'
import {
  SingleColumnSheetArea,
  SingleColumnSheetBody,
} from '../../containers/SingleSheetV2'
import { BasicInformationTable } from '../../containers/SingleSheetV2/table'
import { useSingleSheetFormValidation } from '../../containers/SingleSheetV2/hooks/formValidation'
import { useSubmitButton } from '../../containers/SingleSheetV2/hooks/submitButton'
import { useSingleSheetKeyBind } from '../../containers/SingleSheetV2/hooks/keyBind'
import { MultiLineTextTabDef } from '../../containers/SingleSheetV2/tab/model'
import { useApplicationFunctionUuid } from '../../hooks/useApplicationFunctionUuid'
import {
  useRegistrationStatusOptions,
  useTimezoneOptions,
} from '../../hooks/useCustomEnumSelectOptions'
import { useMeBasicInformationTableDef } from './components/basicInformationTableDef'
import { MeSubHeader } from './components/subheader'
import { useMeEditData } from './hooks/meEditData'
import { useMeModelModifier } from './hooks/meModelModifier'

// この単票は複数行テキストのタブを持たない。バリデーションフックの参照を安定させ
// セルのメモ化を壊さないよう、空配列を共有する。
const NO_MULTILINE_TEXT_TAB_DEFS: MultiLineTextTabDef[] = []

const doNothing = () => undefined

// 自分のユーザー情報画面。更新のみで、作成・削除の導線を持たない。
export const MeEdit = () => {
  const {
    isLoading,
    isSubmitting,
    edited,
    editedFormData,
    formModel,
    entity,
    onChangeMe,
    submit,
    reload,
  } = useMeEditData()
  const timezoneOptions = useTimezoneOptions()
  const registrationStatusOptions = useRegistrationStatusOptions()
  const tableDef = useMeBasicInformationTableDef({
    timezoneOptions,
    registrationStatusOptions,
  })
  const functionUuid = useApplicationFunctionUuid(
    APPLICATION_FUNCTION_EXTERNAL_ID.ME_EDIT
  )
  const {
    validate,
    validateAllProperties,
    validationErrors,
    hasValidationErrors,
  } = useSingleSheetFormValidation(tableDef, NO_MULTILINE_TEXT_TAB_DEFS, edited)
  const formModelModifiers = useMeModelModifier(onChangeMe, validate)
  const { onClickSubmitButton, submitButtonIsActive } = useSubmitButton(
    submit,
    edited,
    formModel,
    hasValidationErrors,
    validateAllProperties
  )
  const isBusy = isLoading || isSubmitting
  // 再取得中はeditedと前回の取得結果が残ったままのため、リロードで破棄を選んだ
  // 直後に保存すると破棄したはずの編集内容が送信されてしまう。また保存後の再取得に
  // 失敗して単票を隠している間は差分の基準が無く、保存しても何も送られない。
  // どちらも編集が残っていても保存できないようにする。
  const canSubmit = submitButtonIsActive && !isBusy && entity !== undefined
  // ショートカットキーでも、隠れた単票の入力チェックの警告を出したりしないよう
  // ボタンと同じ条件で保存を止める。
  const handleSubmit = canSubmit ? onClickSubmitButton : doNothing
  useSingleSheetKeyBind(handleSubmit, functionUuid)

  // この機能の権限が無いユーザーには画面を表示しない。
  if (!functionUuid) return <></>

  return (
    <PageArea>
      <Loading isLoading={isLoading} />
      {/* 取得に失敗して単票を描画できない場合でも画面内のリロードで復帰できるよう、
          サブヘッダーは取得結果に依らず表示する。 */}
      <MeSubHeader
        canSubmit={canSubmit}
        onSubmit={handleSubmit}
        onReload={reload}
        isEdited={edited}
        isBusy={isBusy}
        userUuid={entity?.uuid}
        registrationStatus={entity?.registrationStatus}
      />
      {entity && (
        <SingleColumnSheetArea>
          <SingleColumnSheetBody>
            <BasicInformationTable
              dataUuid={entity.uuid}
              tableDef={tableDef}
              formModel={formModel}
              formModelModifiers={formModelModifiers}
              editedFormData={editedFormData}
              validationErrors={validationErrors}
            />
          </SingleColumnSheetBody>
        </SingleColumnSheetArea>
      )}
    </PageArea>
  )
}
